import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DedupePanel } from "../components/DedupePanel";
import {
  ShieldCheck, Clock, Database, Check, X, Pencil, Copy, ChevronLeft, ChevronRight, Filter, Search, Download,
} from "lucide-react";

const IQA = ["Culture", "Entertainment", "History", "Lifestyle", "Media", "Sciences", "Sport & Games", "World"];
const LIMIT = 25;

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white border border-[#D8CEB9] rounded-sm p-4 flex items-center gap-3">
    <Icon className={`w-5 h-5 ${tone}`} />
    <div>
      <p className="font-mono-cv text-[10px] uppercase tracking-widest text-[#7A6C5D]">{label}</p>
      <p className="font-serif-display text-xl font-bold text-[#1A1615]">{value}</p>
    </div>
  </div>
);

export default function AdminTrivia() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ category: "", source: "", approved: "", q: "", review: "" });
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, questions: [] });
  const [pendingCount, setPendingCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [dupes, setDupes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screening, setScreening] = useState(false);

  useEffect(() => { if (ready && !user) nav("/admin", { replace: true }); }, [ready, user, nav]);

  const loadStats = useCallback(() => {
    api.get("/trivia/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip, limit: LIMIT };
    if (filters.category) params.category = filters.category;
    if (filters.source) params.source = filters.source;
    if (filters.approved) params.approved = filters.approved;
    if (filters.review) params.review = filters.review;
    if (filters.q) params.q = filters.q;
    api.get("/trivia/questions", { params })
      .then((r) => { setData(r.data); setSelected(new Set()); })
      .finally(() => setLoading(false));
    // How many still need approval within the current (non-approval) filter.
    const pendingParams = { skip: 0, limit: 1, approved: "N" };
    if (filters.category) pendingParams.category = filters.category;
    if (filters.source) pendingParams.source = filters.source;
    if (filters.review) pendingParams.review = filters.review;
    if (filters.q) pendingParams.q = filters.q;
    api.get("/trivia/questions", { params: pendingParams })
      .then((r) => setPendingCount(r.data.total || 0))
      .catch(() => {});
  }, [skip, filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setSkip(0); setFilters((f) => ({ ...f, [k]: v })); };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = data.questions.length > 0 && data.questions.every((q) => selected.has(q.tv_id));
  const toggleAll = () => setSelected((s) => {
    if (allOnPage) return new Set();
    return new Set(data.questions.map((q) => q.tv_id));
  });

  const approve = async (ids, approved) => {
    if (!ids.length) return;
    await api.post("/trivia/approve", { ids, approved });
    toast.success(`${ids.length} ${approved ? "approved" : "unapproved"}`);
    load(); loadStats();
  };

  const approveAllMatching = async (approved) => {
    const f = {};
    if (filters.category) f.category = filters.category;
    if (filters.source) f.source = filters.source;
    if (filters.review) f.review = filters.review;
    if (filters.q) f.q = filters.q;
    // Target the actionable subset: approving affects pending rows, rejecting affects live rows.
    f.approved = approved ? "N" : "Y";
    const n = approved ? pendingCount : data.total;
    if (!n) { toast.info(approved ? "Nothing left to approve" : "Nothing to reject"); return; }
    if (!window.confirm(`${approved ? "Approve" : "Reject"} ALL ${n} ${approved ? "pending" : "live"} questions matching the current filter?`)) return;
    const r = await api.post("/trivia/approve", { ids: [], approved, filter: f });
    toast.success(`${r.data.modified} questions updated`);
    load(); loadStats();
  };

  const loadDupes = async () => {
    const r = await api.get("/trivia/duplicates", { params: { category: filters.category || undefined, limit: 50 } });
    setDupes(r.data);
  };

  const deleteTrivia = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Permanently DELETE ${ids.length} question${ids.length > 1 ? "s" : ""} from the database? This cannot be undone.`)) return;
    try {
      const r = await api.post("/trivia/delete", { ids });
      toast.success(`${r.data.deleted} question(s) deleted`);
      await loadDupes();
      load(); loadStats();
    } catch {
      toast.error("Delete failed");
    }
  };

  const [exporting, setExporting] = useState(false);

  const exportTrivia = async () => {
    setExporting(true);
    try {
      const res = await api.get("/trivia/admin/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `${today}_curiovault_trivia_export.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Trivia exported to CSV");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const screenWithAI = async () => {
    setScreening(true);
    try {
      const r = await api.post("/trivia/admin/screen", null, { params: { limit: 10, category: filters.category || undefined, source: filters.source || undefined } });
      const rt = r.data.routing || {};
      toast.success(`Screened ${r.data.screened}: ${rt.auto_approved || 0} auto-approved, ${rt.needs_quick_review || 0} quick, ${rt.needs_full_review || 0} full`);
      load(); loadStats();
    } catch {
      toast.error("Screening failed");
    } finally {
      setScreening(false);
    }
  };

  const pages = Math.ceil(data.total / LIMIT) || 1;
  const page = Math.floor(skip / LIMIT) + 1;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className="caption-label mb-1">Curator Desk</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#1A1615]">Trivia Workbench</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/curios" className="font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535]">Curios</Link>
            <button data-testid="screen-ai-btn" onClick={screenWithAI} disabled={screening} className="inline-flex items-center gap-1.5 font-ui text-sm font-semibold px-4 py-2 border border-[#9E4535] bg-[#9E4535] text-white rounded-sm hover:bg-[#82382B] disabled:opacity-50"><ShieldCheck className="w-4 h-4" /> {screening ? "Screening…" : "Screen 10 with AI"}</button>
            <button data-testid="export-trivia-btn" onClick={exportTrivia} disabled={exporting} className="inline-flex items-center gap-1.5 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535] disabled:opacity-50"><Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export Trivia"}</button>
            <button data-testid="dupes-btn" onClick={loadDupes} className="inline-flex items-center gap-1.5 font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm text-[#4A4036] hover:border-[#9E4535]"><Copy className="w-4 h-4" /> Find duplicates</button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Database} label="Total" value={stats.total.toLocaleString()} tone="text-[#4A4036]" />
            <StatCard icon={ShieldCheck} label="Approved (live)" value={stats.approved.toLocaleString()} tone="text-[#2F6B33]" />
            <StatCard icon={Clock} label="Pending" value={stats.pending.toLocaleString()} tone="text-[#8A5A00]" />
            <StatCard icon={Filter} label="Sources" value={Object.keys(stats.by_source).length} tone="text-[#9E4535]" />
          </div>
        )}

        <DedupePanel onApplied={() => { loadStats(); load(); }} />

        {/* Filters */}
        <div className="bg-white border border-[#D8CEB9] rounded-sm p-4 mb-4 flex flex-wrap items-center gap-3">
          <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilter("category", e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-3 py-2 bg-white">
            <option value="">All categories</option>
            {IQA.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-source" value={filters.source} onChange={(e) => setFilter("source", e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-3 py-2 bg-white">
            <option value="">All sources</option>
            {stats && Object.keys(stats.by_source).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-approved" value={filters.approved} onChange={(e) => setFilter("approved", e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-3 py-2 bg-white">
            <option value="">All statuses</option>
            <option value="N">Pending only</option>
            <option value="Y">Approved only</option>
          </select>
          <select data-testid="filter-review" value={filters.review} onChange={(e) => setFilter("review", e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-3 py-2 bg-white">
            <option value="">All AI results</option>
            <option value="auto_approved">AI 3/3 (auto)</option>
            <option value="needs_quick_review">AI 2/3 (quick)</option>
            <option value="needs_full_review">AI ≤1/3 (full)</option>
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B8AC9C]" />
            <input data-testid="filter-search" value={filters.q} onChange={(e) => setFilter("q", e.target.value)} placeholder="Search question text…" className="w-full font-ui text-sm border border-[#D8CEB9] rounded-sm pl-9 pr-3 py-2" />
          </div>
        </div>

        {/* Bulk actions */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <label className="flex items-center gap-2 font-ui text-sm text-[#4A4036]">
            <input type="checkbox" data-testid="select-all" checked={allOnPage} onChange={toggleAll} /> Select page
          </label>
          <span className="font-mono-cv text-xs text-[#7A6C5D]">{selected.size} selected</span>
          <button data-testid="bulk-approve" disabled={!selected.size} onClick={() => approve([...selected], true)} className="inline-flex items-center gap-1.5 font-ui text-sm font-semibold px-3 py-1.5 bg-[#2F6B33] text-white rounded-sm disabled:opacity-40"><Check className="w-4 h-4" /> Approve</button>
          <button data-testid="bulk-reject" disabled={!selected.size} onClick={() => approve([...selected], false)} className="inline-flex items-center gap-1.5 font-ui text-sm font-semibold px-3 py-1.5 bg-[#8A1E1E] text-white rounded-sm disabled:opacity-40"><X className="w-4 h-4" /> Reject</button>
          <div className="flex-1" />
          {filters.approved === "Y" ? (
            <button data-testid="reject-all-matching" onClick={() => approveAllMatching(false)} className="font-ui text-xs font-semibold px-3 py-1.5 border border-[#8A1E1E] text-[#8A1E1E] rounded-sm hover:bg-[#F7E9E9]">Reject all {data.total.toLocaleString()} live matching</button>
          ) : (
            <button data-testid="approve-all-matching" disabled={pendingCount === 0} onClick={() => approveAllMatching(true)} className="font-ui text-xs font-semibold px-3 py-1.5 border border-[#2F6B33] text-[#2F6B33] rounded-sm hover:bg-[#EAF3EA] disabled:opacity-40 disabled:cursor-not-allowed">
              {pendingCount === 0 ? "All matching approved" : `Approve all ${pendingCount.toLocaleString()} pending`}
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#D8CEB9] rounded-sm divide-y divide-[#EAE2D2]" data-testid="trivia-list">
          {loading ? <p className="p-6 font-body text-[#7A6C5D]">Loading…</p> : data.questions.map((q) => (
            <div key={q.tv_id} className="p-4 flex gap-3" data-testid={`trivia-row-${q.tv_id}`}>
              <input type="checkbox" className="mt-1" checked={selected.has(q.tv_id)} onChange={() => toggleSel(q.tv_id)} data-testid={`select-${q.tv_id}`} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono-cv text-[10px] text-[#B8AC9C]">{q.tv_id}</span>
                  <span className="font-mono-cv text-[10px] bg-[#F3EDE0] px-1.5 py-0.5 rounded">{q.category} / {q.subcategory}</span>
                  <span className="font-mono-cv text-[10px] text-[#7A6C5D]">D{q.difficulty} · {q.source}</span>
                  {typeof q.ai_consensus_score === "number" && (
                    <span className={`font-mono-cv text-[10px] px-1.5 py-0.5 rounded-full border ${q.ai_consensus_score === 3 ? "text-[#2F6B33] bg-[#EAF3EA] border-[#BFD9BF]" : q.ai_consensus_score === 2 ? "text-[#8A5A00] bg-[#FBF1DD] border-[#E4D3A8]" : "text-[#8A1E1E] bg-[#F7E9E9] border-[#E4B8B8]"}`} title="AI consensus">AI {q.ai_consensus_score}/3</span>
                  )}
                  {q.approved
                    ? <span className="inline-flex items-center gap-1 font-ui text-[10px] font-semibold text-[#2F6B33] bg-[#EAF3EA] border border-[#BFD9BF] px-1.5 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> Live</span>
                    : <span className="inline-flex items-center gap-1 font-ui text-[10px] font-semibold text-[#8A5A00] bg-[#FBF1DD] border border-[#E4D3A8] px-1.5 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Pending</span>}
                </div>
                <p className="font-serif-display text-base font-semibold text-[#1A1615]">{q.question}</p>
                <p className="font-body text-sm text-[#4A4036] mt-0.5">✓ {q.correct_answer} <span className="text-[#B8AC9C]">| {(q.options || []).filter((o) => o !== q.correct_answer).join(" · ")}</span></p>
              </div>
              <div className="flex items-start gap-1 shrink-0">
                <button data-testid={`edit-${q.tv_id}`} onClick={() => setEditing(q)} className="p-2 text-[#7A6C5D] hover:text-[#9E4535]" title="Edit"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`toggle-${q.tv_id}`} onClick={() => approve([q.tv_id], !q.approved)} className={`font-ui text-xs font-semibold px-3 py-1.5 rounded-sm ${q.approved ? "bg-[#FBF1DD] text-[#8A5A00]" : "bg-[#2F6B33] text-white"}`}>
                  {q.approved ? "Unapprove" : "Approve"}
                </button>
              </div>
            </div>
          ))}
          {!loading && data.questions.length === 0 && <p className="p-6 font-body text-[#7A6C5D]">No questions match these filters.</p>}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 font-ui text-sm text-[#4A4036]">
          <span>Page {page} of {pages} · {data.total.toLocaleString()} total</span>
          <div className="flex gap-2">
            <button data-testid="prev-page" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} className="p-2 border border-[#D8CEB9] rounded-sm disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="next-page" disabled={page >= pages} onClick={() => setSkip(skip + LIMIT)} className="p-2 border border-[#D8CEB9] rounded-sm disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {editing && <EditModal q={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); loadStats(); }} />}
      {dupes && <DupesModal data={dupes} onClose={() => setDupes(null)} onReject={(ids) => approve(ids, false)} onDelete={deleteTrivia} />}
    </Layout>
  );
}

function EditModal({ q, onClose, onSaved }) {
  const [form, setForm] = useState({
    question: q.question, options: [...(q.options || ["", "", "", ""])], correct_answer: q.correct_answer,
    category: q.category, subcategory: q.subcategory, difficulty: q.difficulty,
    fun_fact: q.fun_fact || "", sources: (q.sources || []).join("\n"), approved: q.approved,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOpt = (i, v) => setForm((f) => { const o = [...f.options]; const wasCorrect = o[i] === f.correct_answer; o[i] = v; return { ...f, options: o, correct_answer: wasCorrect ? v : f.correct_answer }; });

  const save = async () => {
    await api.patch(`/trivia/questions/${q.tv_id}`, {
      question: form.question, options: form.options, correct_answer: form.correct_answer,
      category: form.category, subcategory: form.subcategory, difficulty: Number(form.difficulty),
      fun_fact: form.fun_fact, sources: form.sources.split("\n").map((s) => s.trim()).filter(Boolean),
      approved: form.approved,
    });
    toast.success("Saved");
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div className="bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-serif-display text-2xl font-bold text-[#1A1615] mb-4">Edit {q.tv_id}</h2>
        <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Question</label>
        <textarea data-testid="edit-question" value={form.question} onChange={(e) => set("question", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-3 py-2 font-body text-sm mb-3" rows={2} />
        {q.ai_answers_json && (
          <div className="mb-3 bg-[#F3EDE0] border border-[#D8CEB9] rounded-sm p-3" data-testid="ai-answers">
            <p className="font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-2">AI consensus {q.ai_consensus_score}/3 · {q.review_status}</p>
            {Object.entries(q.ai_answers_json).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 font-body text-sm text-[#4A4036]">
                <span className={v.match ? "text-[#2F6B33]" : "text-[#8A1E1E]"}>{v.match ? "✓" : "✗"}</span>
                <span className="font-mono-cv text-[10px] uppercase w-14 shrink-0">{k}</span>
                <span className="truncate">{v.answer}</span>
              </div>
            ))}
          </div>
        )}
        <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Options (select the correct one)</label>
        {form.options.map((o, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <input type="radio" name="correct" checked={form.correct_answer === o && o !== ""} onChange={() => set("correct_answer", o)} data-testid={`edit-correct-${i}`} />
            <input value={o} onChange={(e) => setOpt(i, e.target.value)} className="flex-1 border border-[#D8CEB9] rounded-sm px-3 py-1.5 font-body text-sm" data-testid={`edit-option-${i}`} />
          </div>
        ))}
        <div className="grid grid-cols-3 gap-3 my-3">
          <div>
            <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-2 font-body text-sm bg-white">{IQA.map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <div>
            <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Subcategory</label>
            <input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-2 font-body text-sm" />
          </div>
          <div>
            <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Difficulty</label>
            <select value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-2 font-body text-sm bg-white">{[1, 2, 3].map((d) => <option key={d} value={d}>{d}</option>)}</select>
          </div>
        </div>
        <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Fun fact</label>
        <textarea data-testid="edit-funfact" value={form.fun_fact} onChange={(e) => set("fun_fact", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-3 py-2 font-body text-sm mb-3" rows={2} />
        <label className="block font-mono-cv text-xs uppercase tracking-widest text-[#7A6C5D] mb-1">Sources (one per line)</label>
        <textarea value={form.sources} onChange={(e) => set("sources", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-3 py-2 font-body text-sm mb-3" rows={2} />
        <label className="flex items-center gap-2 font-ui text-sm text-[#4A4036] mb-4">
          <input type="checkbox" checked={form.approved} onChange={(e) => set("approved", e.target.checked)} data-testid="edit-approved" /> Approved (serve live)
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm">Cancel</button>
          <button data-testid="edit-save" onClick={save} className="font-ui text-sm font-semibold px-5 py-2 bg-[#9E4535] text-white rounded-sm">Save</button>
        </div>
      </div>
    </div>
  );
}

function DupesModal({ data, onClose, onReject, onDelete }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="dupes-modal">
      <div className="bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-serif-display text-2xl font-bold text-[#1A1615] mb-1">Duplicate groups</h2>
        <p className="font-body text-sm text-[#7A6C5D] mb-4">{data.duplicate_groups} groups of questions with identical wording.</p>
        {data.groups.length === 0 && <p className="font-body text-sm text-[#4A4036]">No duplicates found.</p>}
        {data.groups.map((g, i) => (
          <div key={i} className="border border-[#D8CEB9] rounded-sm p-3 mb-3">
            <p className="font-serif-display text-sm font-semibold text-[#1A1615] mb-2">{g.count}× — {g.questions[0].question}</p>
            <div className="space-y-1.5">
              {g.questions.map((q, qi) => (
                <div key={q.tv_id} className="flex items-center justify-between gap-2 font-mono-cv text-xs text-[#7A6C5D]">
                  <span>{q.tv_id} · {q.category} · {q.approved ? "Live" : "Pending"}{qi === 0 ? " · (keep)" : ""}</span>
                  <button
                    onClick={() => onDelete([q.tv_id])}
                    data-testid={`dup-trivia-delete-${q.tv_id}`}
                    className="shrink-0 font-ui text-[11px] font-semibold px-2 py-1 border border-[#8A1E1E] text-[#8A1E1E] rounded-sm hover:bg-[#8A1E1E] hover:text-white transition-colors"
                  >Delete</button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => onReject(g.questions.slice(1).map((q) => q.tv_id))} className="font-ui text-xs font-semibold px-3 py-1.5 border border-[#D8CEB9] text-[#4A4036] rounded-sm hover:border-[#9E4535]">Reject all but first</button>
              <button onClick={() => onDelete(g.questions.slice(1).map((q) => q.tv_id))} data-testid={`dup-delete-group-${i}`} className="font-ui text-xs font-semibold px-3 py-1.5 bg-[#8A1E1E] text-white rounded-sm hover:bg-[#6f1818] transition-colors">Delete all but first</button>
            </div>
          </div>
        ))}
        <div className="flex justify-end mt-2"><button onClick={onClose} className="font-ui text-sm font-semibold px-4 py-2 border border-[#D8CEB9] rounded-sm">Close</button></div>
      </div>
    </div>
  );
}
